import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, MessageSquare, ShieldCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1657346088167-b982455bf29a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxleHRlcmlvciUyMGhvdXNlJTIwcGFpbnRpbmclMjByZW5vdmF0aW9uJTIwd2hpdGUlMjBtb2Rlcm58ZW58MHx8fHwxNzg5ODU3NTU2fDA&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "20 YEARS OF", cls: "text-zinc-900" },
  { text: "CRAFTSMAN", cls: "text-red-600" },
  { text: "PRECISION.", cls: "text-zinc-900" },
];

const ease = [0.16, 1, 0.3, 1];

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-white pt-[72px]"
    >
      <div className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Freshly painted modern white home exterior"
          style={{ y: imgY, scale: imgScale }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/55 to-white/10" />
      </div>

      <motion.div
        data-testid="hero-trust-pill"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.7, ease }}
        className="absolute top-24 sm:top-28 right-4 sm:right-8 lg:right-12 z-10"
      >
        <span className="inline-flex items-center gap-2 bg-zinc-950 text-white font-mono-x text-[10px] sm:text-xs tracking-[0.2em] uppercase px-4 py-2.5">
          <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
          Est. 2004 — San Angelo, TX
        </span>
        <span
          data-testid="hero-insured-pill"
          className="mt-2 flex items-center justify-end gap-2 bg-white text-zinc-900 font-mono-x text-[10px] sm:text-xs tracking-[0.2em] uppercase px-4 py-2.5 border-2 border-zinc-900"
        >
          <ShieldCheck size={14} className="text-red-600" />
          Fully Insured
        </span>
      </motion.div>

      <div className="relative z-10 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto w-full pb-14 sm:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-5"
          data-testid="hero-eyebrow"
        >
          Xtra Clean Carpet Clean &amp; More
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display font-black tracking-tight leading-[0.92] text-[13vw] sm:text-7xl lg:text-[6.5rem]"
        >
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subhead"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease }}
          className="mt-6 max-w-xl text-base sm:text-lg text-zinc-700 leading-relaxed"
        >
          Interior &amp; exterior painting, fine wood refinishing, floor sales
          &amp; installation, and deep floor cleaning — every surface refined
          to perfection. Every quote free.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7, ease }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-get-quote-btn"
            onClick={onQuote}
            className="group flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-7 py-4 font-mono-x text-sm font-semibold uppercase tracking-wider transition-colors duration-200 shadow-xl shadow-red-600/25 active:scale-[0.98]"
          >
            Request Free Quote
            <ArrowDownRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </button>
          <a
            data-testid="hero-call-now-btn"
            href="tel:+13252345947"
            className="flex items-center gap-3 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-all duration-200 px-7 py-4 font-mono-x text-sm font-semibold uppercase tracking-wider active:scale-[0.98]"
          >
            <Phone size={16} /> Call 325-234-5947
          </a>
          <a
            data-testid="hero-text-us-btn"
            href="sms:+13252345947"
            className="flex items-center gap-3 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all duration-200 px-7 py-4 font-mono-x text-sm font-semibold uppercase tracking-wider active:scale-[0.98]"
          >
            <MessageSquare size={16} /> Text Us
          </a>
        </motion.div>
      </div>
    </section>
  );
}
